import { SyncDescriptor } from "./descriptors.js";

export interface ServiceIdentifier<T> {
    (...args: any[]): void;
    type: T;
}
export namespace _util {

    export const serviceIds = new Map<string, ServiceIdentifier<any>>();

    export const DI_TARGET = '$di$target';
    export const DI_DEPENDENCIES = '$di$dependencies';

    export function getServiceDependencies(ctor: DI_TARGET_OBJ): { id: ServiceIdentifier<any>; index: number }[] {
        return ctor[DI_DEPENDENCIES] || [];
    }

    export interface DI_TARGET_OBJ extends Function {
        [DI_TARGET]: Function;
        [DI_DEPENDENCIES]: { id: ServiceIdentifier<any>; index: number }[];
    }
}
function storeServiceDependency(id: ServiceIdentifier<unknown>, target: Function, index: number): void {
    if ((target as _util.DI_TARGET_OBJ)[_util.DI_TARGET] === target) {
        (target as _util.DI_TARGET_OBJ)[_util.DI_DEPENDENCIES].push({ id, index });
    } else {
        (target as _util.DI_TARGET_OBJ)[_util.DI_DEPENDENCIES] = [{ id, index }];
        (target as _util.DI_TARGET_OBJ)[_util.DI_TARGET] = target;
    }
}



export function createDecorator<T>(serviceId: string): ServiceIdentifier<T> {
    if (_util.serviceIds.has(serviceId)) {
        return _util.serviceIds.get(serviceId)!;
    }

    const id = function (target: Function, key: string, index: number) {
        if (arguments.length !== 3) {
            throw new Error('@IServiceName-decorator can only be used to decorate a parameter');
        }
        storeServiceDependency(id, target, index);
    } as ServiceIdentifier<T>;

    id.toString = () => serviceId;

    _util.serviceIds.set(serviceId, id);
    return id;
}

export function refineServiceDecorator<T1, T extends T1>(serviceIdentifier: ServiceIdentifier<T1>): ServiceIdentifier<T> {
	return <ServiceIdentifier<T>>serviceIdentifier;
}



export const enum InstantiationType {
	/**
	 * Instantiate this service as soon as a consumer depends on it. _Note_ that this
	 * is more costly as some upfront work is done that is likely not needed
	 */
	Eager = 0,

	/**
	 * Instantiate this service as soon as a consumer uses it. This is the _better_
	 * way of registering a service.
	 */
	Delayed = 1
}


const _registry: [ServiceIdentifier<any>, SyncDescriptor<any>][] = [];
export type BrandedService = { _serviceBrand: undefined };

export function registerSingleton<T, Services extends BrandedService[]>(id: ServiceIdentifier<T>, ctor: new (...services: Services) => T, supportsDelayedInstantiation: InstantiationType): void;
export function registerSingleton<T, Services extends BrandedService[]>(id: ServiceIdentifier<T>, descriptor: SyncDescriptor<any>): void;
export function registerSingleton<T, Services extends BrandedService[]>(id: ServiceIdentifier<T>, ctorOrDescriptor: { new(...services: Services): T } | SyncDescriptor<any>, supportsDelayedInstantiation?: boolean | InstantiationType): void {
    if (!(ctorOrDescriptor instanceof SyncDescriptor)) {
        ctorOrDescriptor = new SyncDescriptor<T>(ctorOrDescriptor as new (...args: any[]) => T, [], Boolean(supportsDelayedInstantiation));
    }

    _registry.push([id, ctorOrDescriptor]);
}

export function getSingletonServiceDescriptors(): [ServiceIdentifier<any>, SyncDescriptor<any>][] {
	return _registry;
}
